import { Capacitor } from "@capacitor/core";

/**
 * Apre una pagina di un altro sito SENZA uscire dall'app.
 *
 * I costruttori (notifier.it, ajax.systems) mandano X-Frame-Options: SAMEORIGIN:
 * le loro pagine non si possono incorniciare dentro la nostra. Quello che si
 * può fare:
 *  - nelle app native (iOS/Android): il browser in-app di sistema
 *    (SFSafariViewController / Chrome Custom Tabs), che si apre SOPRA l'app e
 *    si chiude con «Fine»: l'installatore non lascia mai Datacom AI;
 *  - nella PWA e sul web: una scheda nuova (l'unica via che il browser
 *    concede; tornare indietro è un tocco).
 */
export async function apriEsterno(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url, presentationStyle: "popover" });
      return;
    } catch {
      // plugin assente nel guscio nativo vecchio: si ripiega sulla scheda
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Apre la pagina di accesso di Google o Apple, e SU iOS FUNZIONA.
 *
 * IL DIFETTO (Davide dall'iPhone, 08/09/2026: «non parte proprio niente,
 * sembra che non prenda il tocco»). Il tocco arrivava benissimo: era
 * `window.open(url, "_blank")` a non fare nulla. Dentro la vista web di iOS
 * (WKWebView) quella chiamata viene **ignorata in silenzio** se nessuno la
 * raccoglie lato nativo — nessun errore, nessuna finestra, niente da vedere.
 * Su Android invece Capacitor la intercetta e apre il browser: per questo
 * l'APK funzionava e l'iPhone no, con lo stesso identico codice.
 *
 * Senza i due bottoni su iOS l'app non passa la revisione (linea guida 4.8:
 * se c'e' Google deve esserci Apple), quindi questo non era un dettaglio.
 *
 * LA CURA: il browser di sistema aperto dal plugin, come si fa gia' per le
 * pagine dei costruttori. Su iOS e' SFSafariViewController, su Android una
 * Chrome Custom Tab: sono browser veri, e Google **pretende** un browser vero
 * per OAuth (dentro una webview rifiuta l'accesso). Il ripiego su
 * `window.open` resta per il guscio vecchio e per il web.
 */
export async function apriPerAccesso(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
      return;
    } catch {
      // guscio senza il plugin: meglio provare che lasciare il bottone muto
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Chiude il browser di sistema rimasto aperto sopra l'app.
 *
 * Quando il giro con Google o Apple finisce, il telefono riapre l'app col
 * collegamento di ritorno, ma la finestra del browser resta li' davanti: chi
 * ha appena fatto l'accesso vede una pagina bianca e crede che sia bloccato.
 * Va chiusa a mano. Se non c'e' niente da chiudere, non succede niente.
 */
export async function chiudiBrowserInApp(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    // niente da chiudere, o guscio senza plugin: non e' un errore
  }
}
